import type { ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Heading } from '../Heading/Heading';
import s from './PageHeader.module.css';

export type PageHeaderProps = {
  title: ReactNode;
  /** Secondary line, e.g. "Christmas 2026 · in 97 days". */
  meta?: ReactNode;
  /** Leading visual (occasion emoji, avatar). */
  leading?: ReactNode;
  /** Actions. On phones prefer a single overflow Menu here. */
  actions?: ReactNode;
  /** Optional back link/breadcrumb above the title (desktop sub-pages). */
  above?: ReactNode;
  className?: string;
};

/** The page's h1 + context. Focus lands here on navigation (docs/05). */
export function PageHeader({ title, meta, leading, actions, above, className }: PageHeaderProps) {
  return (
    <header className={cx(s.header, className)}>
      {above ? <div className={s.above}>{above}</div> : null}
      <div className={s.row}>
        {leading ? <div className={s.leading}>{leading}</div> : null}
        <div className={s.titles}>
          <Heading level={1} tabIndex={-1} className={s.title}>
            {title}
          </Heading>
          {meta ? <div className={s.meta}>{meta}</div> : null}
        </div>
        {actions ? <div className={s.actions}>{actions}</div> : null}
      </div>
    </header>
  );
}

export type SectionHeaderProps = {
  title: ReactNode;
  description?: ReactNode;
  /** A small trailing action, e.g. "See all ›". */
  action?: ReactNode;
  level?: 2 | 3;
  className?: string;
};

export function SectionHeader({
  title,
  description,
  action,
  level = 2,
  className,
}: SectionHeaderProps) {
  return (
    <div className={cx(s.section, className)}>
      <div className={s.titles}>
        <Heading level={level}>{title}</Heading>
        {description ? <p className={s.meta}>{description}</p> : null}
      </div>
      {action ? <div className={s.sectionAction}>{action}</div> : null}
    </div>
  );
}
